import { z } from "zod";
import { BRIDGE } from "../../../config/bridge";
import { HistoryApi, HistoryApiError, itemSchema, mutationSchema, claimSchema, sameIdentity, type HistoryItem, type HistoryMutation, type ClaimEstimate } from "../api/history";
import type { ExclusiveLock } from "./journal";
import type { BridgeRecord, ClaimIntent, ClaimStatus, HistoryRow, RecoveryIntent } from "./types";
import { claimIntentSchema } from "./schemas";

type StoragePort = Pick<Storage, "length" | "key" | "getItem" | "setItem" | "removeItem">;
const entrySchema = z.object({ desired: mutationSchema.omit({ claims: true }).extend({ claims: z.array(claimSchema).optional() }), current: itemSchema.nullable(), flight: mutationSchema.nullable(),
  retryAt: z.number(), failures: z.number(), needsVerification: z.boolean(), limited: z.boolean(), error: z.string().nullable(),
  conflictAt: z.number().nullable().default(null), aliases: z.record(z.string(), claimIntentSchema).default({}) });
type Entry = z.infer<typeof entrySchema>;
const equal = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const size = (v: unknown) => new TextEncoder().encode(JSON.stringify(v)).length;
const observationEqual = (a: ClaimStatus, b: ClaimStatus | undefined) => b !== undefined && equal({ ...a, lastVerified: undefined }, { ...b, lastVerified: undefined });
/** One durable entry per source/message. Exact uncertain HTTP payload survives reloads.
 * Remote reports are only revision/identity hints, never local chain observations. */
export class BridgeHistorySync {
  readonly prefix: string;
  error: string | null = null;
  private queueError: string | null = null;
  constructor(private recoveryPrefix: string, readonly api: HistoryApi, private storage: StoragePort | null, private lock: ExclusiveLock) {
    this.prefix = `${recoveryPrefix}sync:${encodeURIComponent(api.base)}:`;
  }
  canRecoverSource(sourceHash: string) {
    try {
      const environment = this.storage!.getItem(`${this.recoveryPrefix}sync:environment:${sourceHash}`);
      // Automatic source recovery imports every message, including those
      // already assigned to a legacy outbox in a different environment.
      return (environment === null || environment === this.api.base) &&
        !this.allEnvironmentEntries().some(([key, entry]) => !key.startsWith(this.prefix) && entry.desired.sourceHash === sourceHash);
    } catch { return false; }
  }
  private bindEnvironment(sourceHash: string) {
    const key = `${this.recoveryPrefix}sync:environment:${sourceHash}`, previous = this.storage?.getItem(key);
    if (previous != null && previous !== this.api.base) throw new Error("Source recovery belongs to a different API environment; it was retained without moving its reports.");
    if (previous == null) this.storage!.setItem(key, this.api.base);
  }
  private entries(): [string, Entry][] {
    const values: [string, Entry][] = [];
    if (!this.storage) throw new Error("Bridge synchronization storage unavailable");
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key?.startsWith(this.prefix)) values.push([key, entrySchema.parse(JSON.parse(this.storage.getItem(key)!))]);
    }
    return values;
  }
  private save(key: string, entry: Entry) { this.storage!.setItem(key, JSON.stringify(entry)); }
  private key(hash: string, rowId?: string) {
    const entries = this.entries().filter(([, e]) => e.desired.sourceHash === hash);
    // The first decoded message adopts the original pending registration.
    return entries.find(([, e]) => e.desired.row?.record.id === rowId)?.[0] ?? entries.find(([, e]) => !e.desired.row)?.[0] ?? `${this.prefix}${rowId ?? hash}`;
  }
  private create(intent: RecoveryIntent, submittedAt: string): Entry {
    if (!intent.sourceHash) throw new Error("Source hash required");
    return { desired: { routeId: BRIDGE.id, account: intent.account, direction: intent.direction, sourceHash: intent.sourceHash,
      recipient: intent.recipient, amountWei: intent.amountWei, submittedAt, clientRequestId: crypto.randomUUID(), expectedRevision: null,
      pending: { state: "awaiting-receipt", checkedAt: null } }, current: null, flight: null, retryAt: 0, failures: 0, needsVerification: false, limited: false, error: null, conflictAt: null, aliases: {} };
  }
  async retain(intent: RecoveryIntent, stillLocal?: () => boolean) {
    try { await this.lock(this.recoveryPrefix, async () => {
      if (stillLocal && !stillLocal()) return;
      this.bindEnvironment(intent.sourceHash!);
      const key = this.key(intent.sourceHash!);
      if (!this.storage!.getItem(key)) this.save(key, this.create(intent, new Date(intent.submittedAt ?? Date.now()).toISOString()));
      this.queueError = null;
    }); } catch (error) { this.error = this.queueError = error instanceof Error ? error.message : "History sync could not be saved. Local recovery is retained; do not resend."; }
  }
  async observe(rows: HistoryRow[], claims: ClaimStatus[] | (() => ClaimStatus[]), estimates?: Record<string, ClaimEstimate | null>, signal?: AbortSignal, stillCurrent?: (row: HistoryRow) => boolean) {
    signal?.throwIfAborted();
    try { await this.lock(this.recoveryPrefix, async () => {
      signal?.throwIfAborted();
      const latestClaims = typeof claims === "function" ? claims() : claims;
      const foreign = this.allEnvironmentEntries().filter(([key]) => !key.startsWith(this.prefix));
      for (const row of rows) {
        if (stillCurrent && !stillCurrent(row) || foreign.some(([, entry]) => entry.desired.sourceHash === row.record.sourceHash &&
          (!entry.desired.row || entry.desired.row.record.id === row.record.id))) continue;
        this.bindEnvironment(row.record.sourceHash);
        const r = row.record, key = this.key(r.sourceHash, r.id);
        const entry = this.storage!.getItem(key) ? entrySchema.parse(JSON.parse(this.storage!.getItem(key)!)) : this.create({
          id: crypto.randomUUID(), account: r.account, direction: r.direction, sourceHash: r.sourceHash, recipient: r.recipient,
          amountWei: r.amountWei, createdAt: Date.now() }, new Date().toISOString());
        // A conflict requires a new RPC observation before constructing a mutation.
        if (entry.needsVerification && (row.checkedAt === null || row.checkedAt <= (entry.conflictAt ?? entry.retryAt))) continue;
        entry.needsVerification = false;
        const { pending: _pending, ...base } = entry.desired; void _pending;
        const { reported: _reported, ...observation } = row; void _reported;
        const observations = latestClaims.filter(c => c.intent.recordId === r.id &&
          !(base.claims ?? []).some(previous => previous.intent.id === c.intent.id &&
            (previous.intent.transactionHash && previous.intent.transactionHash !== c.intent.transactionHash ||
              previous.intent.outcome && previous.intent.outcome !== c.intent.outcome)));
        const observedIds = new Set(observations.map(c => c.intent.id));
        entry.desired = { ...base, recipient: r.recipient, amountWei: r.amountWei, row: observation,
          claims: [...(base.claims ?? []).filter(c => !observedIds.has(c.intent.id)), ...observations], ...(r.direction === "out" && estimates && Object.hasOwn(estimates, r.id) ? { claimEstimate: estimates[r.id] ?? null } : {}) };
        this.reconcileAliases(entry);
        this.save(key, entry);
      }
      this.queueError = null;
    }); } catch (error) { if (!signal?.aborted) this.error = this.queueError = error instanceof Error ? error.message : "History updates could not be queued. Local recovery is retained."; }
  }
  async retainClaim(record: BridgeRecord, intent: ClaimIntent, stillLocal?: () => boolean) {
    try { await this.lock(this.recoveryPrefix, async () => {
      if (stillLocal && !stillLocal()) return;
      this.bindEnvironment(record.sourceHash);
      const key = this.key(record.sourceHash, record.id), raw = this.storage!.getItem(key);
      const entry = raw ? entrySchema.parse(JSON.parse(raw)) : this.create({ id: crypto.randomUUID(), account: record.account,
        direction: record.direction, recipient: record.recipient, amountWei: record.amountWei, sourceHash: record.sourceHash, createdAt: Date.now() }, new Date().toISOString());
      const { pending: _pending, ...base } = entry.desired; void _pending;
      const observed = base.claims?.find(c => equal(c.intent, intent) && c.checkedAt !== null);
      entry.desired = { ...base, row: base.row ?? { record, state: "checking", checkedAt: null },
        claims: [...(base.claims ?? []).filter(c => c.intent.id !== intent.id), observed ?? { intent, state: "checking", checkedAt: null }] };
      this.save(key, entry);
    }); } catch { this.error = this.queueError = "Claim hash retained locally; shared history could not be queued. Do not resend."; }
  }
  /** Alias only wire identity. The original local intent/hash remains recovery data.
   * The caller supplies an RPC-verified association, never a GET status. */
  async linkClaim(record: BridgeRecord, claim: ClaimStatus, current: HistoryItem) {
    if (claim.checkedAt === null || !["claimed", "reverted"].includes(claim.state) || !claimIntentSchema.safeParse(claim.intent).success ||
      !claim.intent.transactionHash || claim.intent.recordId !== record.id || claim.intent.account !== record.account ||
      !sameIdentity({ routeId: BRIDGE.id, account: record.account, direction: record.direction, sourceHash: record.sourceHash, row: { record, state: "checking", checkedAt: null } }, current) ||
      !current.claims.some(c => c.intent.transactionHash === claim.intent.transactionHash && c.intent.account === record.account && c.intent.recordId === record.id)) return;
    try { await this.lock(this.recoveryPrefix, async () => {
      this.bindEnvironment(record.sourceHash);
      const key = this.key(record.sourceHash, record.id), raw = this.storage!.getItem(key);
      const entry = raw ? entrySchema.parse(JSON.parse(raw)) : this.create({ id: crypto.randomUUID(), account: record.account, direction: record.direction,
        sourceHash: record.sourceHash, recipient: record.recipient, amountWei: record.amountWei, createdAt: Date.now() }, new Date().toISOString());
      const { pending: _pending, ...base } = entry.desired; void _pending;
      entry.desired = { ...base, row: base.row ?? { record, state: "checking", checkedAt: null },
        claims: [...(base.claims ?? []).filter(c => c.intent.id !== claim.intent.id), claim] };
      if (!entry.flight) {
        entry.current = current; entry.needsVerification = true; entry.conflictAt = Date.now() - 1; entry.retryAt = 0;
      }
      // An uncertain flight is replayed verbatim first; only its definite result
      // may change revision/current. Keep this candidate mapping meanwhile.
      const canonical = current.claims.find(c => c.intent.transactionHash === claim.intent.transactionHash && c.intent.account === record.account && c.intent.recordId === record.id)!;
      entry.aliases[claim.intent.id] = canonical.intent;
      this.save(key, entry);
    }); } catch { this.error = this.queueError = "Claim reconciliation could not be saved. Local recovery is retained."; }
  }
  private reconcileAliases(entry: Entry) {
    const current = entry.current;
    if (!current || !entry.desired.row || !sameIdentity(entry.desired, current)) return;
    for (const claim of entry.desired.claims ?? []) {
      if (claim.checkedAt === null || !["claimed", "reverted"].includes(claim.state) || entry.conflictAt !== null && claim.checkedAt <= entry.conflictAt) continue;
      const canonical = claim.intent.transactionHash && current.claims.find(c => c.intent.transactionHash === claim.intent.transactionHash &&
        c.intent.account === claim.intent.account && c.intent.recordId === claim.intent.recordId);
      if (canonical) {
        if (canonical.intent.id === claim.intent.id) delete entry.aliases[claim.intent.id];
        else entry.aliases[claim.intent.id] = canonical.intent;
      }
    }
  }
  private wireClaim(entry: Entry, claim: ClaimStatus): ClaimStatus {
    const canonical = entry.aliases[claim.intent.id];
    return canonical && canonical.transactionHash === claim.intent.transactionHash && canonical.recordId === claim.intent.recordId && canonical.account === claim.intent.account
      ? { ...claim, intent: canonical } : claim;
  }
  private blockedClaims(entry: Entry) {
    // Known wallet rejection/non-submission is local evidence, not a claim receipt
    // status that RPC could recheck. Hash-bearing observations need their own read.
    return (entry.desired.claims ?? []).filter(c => {
      if (c.intent.outcome) return false;
      const wire = this.wireClaim(entry, c);
      const duplicate = c.intent.transactionHash && entry.current?.claims.find(v => v.intent.transactionHash === c.intent.transactionHash &&
        v.intent.account === c.intent.account && v.intent.recordId === c.intent.recordId);
      // A pending/missing receipt has not bound a public alias to this withdrawal.
      if (duplicate && duplicate.intent.id !== wire.intent.id) return true;
      return entry.conflictAt !== null && (c.checkedAt === null || c.checkedAt <= entry.conflictAt) &&
        !observationEqual(wire, entry.current?.claims.find(v => v.intent.id === wire.intent.id));
    });
  }
  async observePending(intent: RecoveryIntent, receiptUnavailable: boolean) {
    try { await this.lock(this.recoveryPrefix, async () => {
      if (!this.canRecoverSource(intent.sourceHash!)) return;
      const key = this.key(intent.sourceHash!), raw = this.storage!.getItem(key);
      if (!raw) return;
      const entry = entrySchema.parse(JSON.parse(raw));
      if (entry.desired.row || entry.current?.row || entry.needsVerification && receiptUnavailable) return;
      if (!receiptUnavailable) entry.needsVerification = false;
      entry.desired.pending = { state: receiptUnavailable ? "unavailable" : "awaiting-receipt", checkedAt: receiptUnavailable ? null : Date.now() };
      this.save(key, entry);
    }); } catch { this.error = this.queueError = "Pending history could not be queued. Local recovery is retained."; }
  }
  private next(entry: Entry): HistoryMutation | null {
    const d = entry.desired, c = entry.current;
    const { claims: _claims, row: _row, pending: _pending, claimEstimate: _eta, ...base } = d; void [_claims, _row, _pending, _eta];
    const body: HistoryMutation = { ...base, clientRequestId: c?.clientRequestId ?? d.clientRequestId, submittedAt: c?.submittedAt ?? d.submittedAt, expectedRevision: c?.revision ?? null };
    if (!d.row && c) { body.recipient = c.recipient; body.amountWei = c.amountWei; }
    if (d.row && !equal({ ...d.row, lastVerified: undefined }, c?.row ? { ...c.row, lastVerified: undefined } : null)) body.row = d.row;
    else if (!c?.row && d.pending && !equal({ ...d.pending, lastVerified: undefined }, c?.pending ? { ...c.pending, lastVerified: undefined } : null)) body.pending = d.pending;
    if (d.claimEstimate !== undefined && !equal(d.claimEstimate, c?.claimEstimate) && (entry.conflictAt === null ||
      (d.claimEstimate ? Date.parse(d.claimEstimate.refresh?.checkedAt ?? d.claimEstimate.calculatedAt) : d.row?.checkedAt ?? 0) > entry.conflictAt)) body.claimEstimate = d.claimEstimate;
    const blocked = new Set(this.blockedClaims(entry).map(claim => claim.intent.id));
    const merged = new Map<string, ClaimStatus>();
    for (const local of d.claims ?? []) {
      if (blocked.has(local.intent.id)) continue;
      const claim = this.wireClaim(entry, local), previous = merged.get(claim.intent.id);
      if (!previous || (claim.checkedAt ?? -1) > (previous.checkedAt ?? -1)) merged.set(claim.intent.id, claim);
    }
    const updates = [...merged.values()].filter(claim => !observationEqual(claim, c?.claims.find(v => v.intent.id === claim.intent.id)) &&
      (!entry.limited || c?.claims.some(v => v.intent.id === claim.intent.id)));
    for (const claim of updates) {
      const proposed = { ...body, claims: [...(body.claims ?? []), claim] };
      if ((body.claims?.length ?? 0) === 20 || size(proposed) > 16 * 1024) break;
      body.claims = proposed.claims;
    }
    if (!body.row && !body.pending && !body.claims?.length && body.claimEstimate === undefined) return null;
    if (size(body) > 16 * 1024) throw new Error("Bridge source report exceeds request size limit; recovery retained.");
    return mutationSchema.parse(body);
  }
  verificationNeeded() {
    try { return this.entries().filter(([, e]) => e.needsVerification || this.blockedClaims(e).some(c => c.intent.transactionHash)).flatMap(([, e]) => e.desired.row ? [e.desired.row.record.id] : []); }
    catch { return []; }
  }
  claimVerificationNeeded() {
    try { return new Set(this.entries().flatMap(([, e]) => this.blockedClaims(e).flatMap(c => c.intent.transactionHash ? [c.intent.id, this.wireClaim(e, c).intent.id] : []))); }
    catch { return new Set<string>(); }
  }
  async flush(signal: AbortSignal) {
    try { await this.lock(`${this.prefix}http`, async () => {
      // Bounded drain, serialize all writes across tabs. New observations coalesce.
      for (const [key] of this.entries().filter(([, e]) => !e.needsVerification && e.retryAt <= Date.now() && (e.flight || this.next(e))).slice(0, 8)) {
        signal.throwIfAborted();
        const request = await this.lock(this.recoveryPrefix, async () => {
          const raw = this.storage!.getItem(key);
          if (!raw) return null;
          const entry = entrySchema.parse(JSON.parse(raw));
          if (entry.needsVerification || entry.retryAt > Date.now()) return null;
          const body = entry.flight ?? this.next(entry);
          if (!body) return null;
          entry.flight = body; this.save(key, entry); // durable before HTTP dispatch
          return { entry, body };
        });
        if (!request) continue;
        const { entry, body } = request;
        try {
          const current = await this.api.post(body, signal);
          if (!sameIdentity(body, current) || body.row && !current.row) throw new Error("Bridge sync identity mismatch");
          entry.current = current; entry.flight = null; entry.failures = 0; entry.retryAt = 0;
          entry.error = entry.limited ? "Claim history limit reached; unsynchronized attempts remain in this browser." : null;
        } catch (error) {
          signal.throwIfAborted();
          const current = error instanceof HistoryApiError ? error.current : undefined;
          if (current && sameIdentity(body, current) && (body.claims ?? []).every(c =>
            !current.claims.some(v => v.intent.id === c.intent.id && (v.intent.recordId !== c.intent.recordId || v.intent.transactionHash && v.intent.transactionHash !== c.intent.transactionHash)))) {
            entry.current = current; entry.flight = null; entry.needsVerification = true; entry.conflictAt = Date.now();
            entry.limited ||= error instanceof HistoryApiError && error.code === "CLAIM_LIMIT_REACHED";
            entry.retryAt = Date.now();
            entry.error = entry.limited ? "Claim history limit reached; unsynchronized attempts remain in this browser." : "History conflict: retained locally; RPC recheck required.";
          } else {
            entry.failures = Math.min(entry.failures + 1, 6);
            entry.retryAt = error instanceof HistoryApiError && !error.retryable ? Number.MAX_SAFE_INTEGER :
              Date.now() + Math.max(30_000 * 2 ** (entry.failures - 1), error instanceof HistoryApiError ? error.retryAfterMs ?? 0 : 0);
            entry.error = "History synchronization unavailable. Transaction recovery is retained; nothing will be resent on-chain.";
          }
        }
        await this.lock(this.recoveryPrefix, async () => {
          // Hash retention/observations can continue while HTTP is in flight.
          // Preserve their newest desired facts when acknowledging the old request.
          const latest = entrySchema.parse(JSON.parse(this.storage!.getItem(key)!));
          entry.desired = latest.desired; entry.aliases = { ...latest.aliases, ...entry.aliases };
          this.reconcileAliases(entry);
          this.save(key, entry);
        });
      }
      this.error = this.entries().find(([, e]) => e.error)?.[1].error ?? this.queueError;
    }); } catch (error) { if (!signal.aborted) this.error = error instanceof Error ? error.message : "History sync unavailable"; }
  }
  private settled(entry: Entry) {
    return !!entry.current && !entry.flight && !entry.needsVerification && !entry.error && this.blockedClaims(entry).length === 0 &&
      this.next(entry) === null && (entry.desired.claims ?? []).every(local => {
        const claim = this.wireClaim(entry, local);
        return entry.current!.claims.some(saved => equal(saved.intent, claim.intent) && saved.state === claim.state);
      });
  }
  private confirms(entry: Entry, row: HistoryRow, claims: ClaimStatus[]) {
    return this.settled(entry) && entry.current?.row?.state === row.state && equal(entry.current.row.record, row.record) &&
      claims.filter(c => c.intent.recordId === row.record.id).every(c => entry.current!.claims.some(v =>
        equal(v.intent, this.wireClaim(entry, c).intent) && v.state === c.state));
  }
  acknowledged(row: HistoryRow, claims: ClaimStatus[]) {
    try { return this.entries().some(([, entry]) => this.confirms(entry, row, claims)); }
    catch { return false; }
  }
  private allEnvironmentEntries() {
    const entries: [string, Entry][] = [];
    const prefix = `${this.recoveryPrefix}sync:`;
    for (let i = 0; i < this.storage!.length; i++) {
      const key = this.storage!.key(i);
      if (key?.startsWith(prefix) && !key.startsWith(`${prefix}environment:`))
        entries.push([key, entrySchema.parse(JSON.parse(this.storage!.getItem(key)!))]);
    }
    return entries;
  }
  // Called under the journal lock, which also protects every outbox write.
  canPruneUnsentClaim(intent: ClaimIntent) {
    if (!this.storage || this.queueError) return false;
    return this.allEnvironmentEntries().every(([, entry]) => {
      const references = [entry.desired.claims, entry.flight?.claims, entry.current?.claims].flatMap(claims => claims ?? [])
        .filter(claim => claim.intent.id === intent.id);
      return !references.length || this.settled(entry) && references.every(claim => equal(claim.intent, intent)) &&
        entry.current!.claims.some(claim => equal(claim.intent, intent));
    });
  }
  // A stranded outbox row still provides a recovery candidate after an interrupted
  // prune. Its reported status never substitutes for an independent chain read.
  recoveryRows() {
    try { return this.entries().flatMap(([, entry]) => entry.desired.row ?
      [{ record: entry.desired.row.record, claims: entry.desired.claims ?? [] }] : []); }
    catch { return []; }
  }
  forgetCompleted(row: HistoryRow, claims: ClaimStatus[], prune: (hasOtherOutbox: boolean) => void, hasRecovery: () => boolean) {
    if (!this.storage || this.queueError) return false;
    const { sourceHash, id } = row.record;
    const marker = `${this.recoveryPrefix}sync:environment:${sourceHash}`;
    const environment = this.storage.getItem(marker);
    if (environment !== null && environment !== this.api.base) return false;
    const entries = this.allEnvironmentEntries().filter(([, entry]) => entry.desired.sourceHash === sourceHash);
    const own = entries.filter(([key, entry]) => key.startsWith(this.prefix) && entry.desired.row?.record.id === id);
    const other = entries.filter(entry => !own.includes(entry));
    if (!own.length || own.some(([, entry]) => !this.confirms(entry, row, claims) ||
      entry.desired.claims?.some(claim => !claim.intent.outcome &&
        (!claim.intent.transactionHash || !claims.some(observed => observed.checkedAt !== null &&
          ["claimed", "reverted"].includes(observed.state) &&
          equal(this.wireClaim(entry, observed).intent, this.wireClaim(entry, claim).intent))))) ||
      other.some(([, entry]) => !entry.desired.row || entry.desired.row.record.id === id)) return false;
    prune(other.length > 0);
    for (const [key] of own) this.storage.removeItem(key);
    if (!other.length && !hasRecovery() && environment === this.api.base) this.storage.removeItem(marker);
    return true;
  }
}
